import * as React from 'react';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import MobileStepper from '@mui/material/MobileStepper';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useTheme } from '@mui/material';
import { Button } from '../button/button';
import { useCustomTranslation } from '../../translation/customTranslation';
import { AlertDialog } from '../alertDialog/alertDialog';

interface SymptomAsessmentSteps {
  type: string;
  title: {
    l10nKey: string;
    l10nValue: string;
  };
  description: {
    l10nKey: string;
    l10nValue: string;
  };
}

interface SymptomAsessmentStepperProps {
  setStarted: () => void;
  steps: SymptomAsessmentSteps[];
  answers: number[];
  handleAnswer: (
    event: React.MouseEvent<HTMLElement>,
    newAnswer: string,
    answerStep: number,
  ) => void;
  handleSubmit: () => void;
  playerFirstName: string;
}

export function SymptomAsessmentStepper({
  setStarted,
  steps,
  answers,
  handleAnswer,
  handleSubmit,
  playerFirstName,
}: SymptomAsessmentStepperProps) {
  const { t } = useTranslation();
  const theme = useTheme();
  const { t: symptomT } = useCustomTranslation('symptom');
  const [activeStep, setActiveStep] = useState<number>(0);
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false);

  const maxSteps = steps.length;
  const answerRange = [0, 1, 2, 3, 4, 5, 6];

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  return (
    <>
      <Grid container>
        <Paper
          square
          elevation={1}
          sx={{
            display: 'flex',
            alignItems: 'center',
            height: 50,
            width: '100%',
            px: 2,
            bgcolor: 'background.default',
          }}
        >
          <Typography variant="h5" fontWeight="bold">
            {symptomT('START_SYMPTOM_CHECK_TITLE')}
          </Typography>
          <IconButton
            aria-label={t('accessibility.CLOSE')}
            sx={{ marginLeft: 'auto' }}
            onClick={() => setShowCloseModal(true)}
          >
            <CloseIcon />
          </IconButton>
        </Paper>

        <Grid container sx={{ mt: 2 }} direction="column" spacing={2}>
          <Grid item sx={{ mt: 2 }}>
            <Typography variant="body1">
              {symptomT(steps[activeStep].title.l10nKey)}
            </Typography>
            <Typography variant="subtitle2" color="secondary">
              {symptomT('START_SYMPTOM_CHECK_SUBTITLE')} {activeStep + 1} /{' '}
              {maxSteps}
            </Typography>
          </Grid>

          <Grid item sx={{ mt: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              {symptomT(steps[activeStep].description.l10nKey, {
                playerFirstName,
              })}
            </Typography>
          </Grid>

          <Grid item>
            <Typography variant="subtitle2">
              {symptomT('START_SYMPTOM_CHECK_LEGEND_NONE')}
            </Typography>
            <Typography variant="subtitle2">
              {symptomT('START_SYMPTOM_CHECK_LEGEND_MILD')}
            </Typography>
            <Typography variant="subtitle2">
              {symptomT('START_SYMPTOM_CHECK_LEGEND_MOD')}
            </Typography>
            <Typography variant="subtitle2">
              {symptomT('START_SYMPTOM_CHECK_LEGEND_SEVERE')}
            </Typography>
          </Grid>

          <Grid container sx={{ px: 2, mt: 4 }} direction="row">
            <ToggleButtonGroup
              value={answers[activeStep]}
              exclusive
              fullWidth
              onChange={(
                event: React.MouseEvent<HTMLElement>,
                newAnswer: string,
              ) => handleAnswer(event, newAnswer, activeStep)}
              aria-label={t('accessibility.ACTIVE_STEP')}
            >
              {answerRange.map((level) => (
                <ToggleButton
                  key={level}
                  value={level.toString()}
                  aria-label={level.toString()}
                  size="small"
                  sx={{
                    minWidth: 50,
                    '&.Mui-selected, &.Mui-selected:hover': {
                      color: theme.palette.primary.contrastText,
                      backgroundColor: theme.palette.primary.light,
                      opacity: 0.8,
                    },
                  }}
                  selected={level === answers[activeStep] ?? false}
                >
                  {level.toString()}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Grid>

          <Grid item sx={{ width: '100%', mt: 2 }}>
            <MobileStepper
              variant="text"
              steps={maxSteps}
              position="static"
              activeStep={activeStep}
              nextButton={<></>}
              backButton={<></>}
            />

            <Button
              size="small"
              variant="contained"
              type="button"
              color="secondary"
              fullWidth
              onClick={() =>
                activeStep === maxSteps - 1 ? handleSubmit() : handleNext()
              }
              disabled={!answers[activeStep] && answers[activeStep] !== 0}
              label={t('common.generic.GENERIC_NAVIGATION_NEXT')}
            />

            <Button
              size="small"
              fullWidth
              onClick={() => (activeStep !== 0 ? handleBack() : setStarted())}
              variant="text"
              type="button"
              label={symptomT('START_SYMPTOM_CHECK_GO_BACK')}
              sx={{ textTransform: 'none', textDecoration: 'underline', mt: 2 }}
            />
          </Grid>
        </Grid>
      </Grid>

      <AlertDialog
        actions
        title={symptomT('SYMPTOM_ASSESSMENT_EXIT_CONFIRM_TITLE')}
        open={showCloseModal}
        setOpen={() => {}}
        content={symptomT('SYMPTOM_ASSESSMENT_EXIT_CONFIRM_CONTENT')}
        confirmLabel={t('CONFIRM')}
        cancelLabel={t('CANCEL')}
        onDismiss={() => setShowCloseModal(false)}
        onConfirm={setStarted}
      />
    </>
  );
}
